import { FaSolidHandshake } from "solid-icons/fa";
import { TbOutlineBriefcase } from "solid-icons/tb";
import { createSignal, Index } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { FreelanceBusinessCard } from "@/domains/freelance/components/business-card/FreelanceBusinessCard";
import type { FreelanceBusinessCardData } from "@/domains/freelance/components/business-card/types";
import { RemotePolicy } from "@/domains/job-offer/create/constants";

/**
 * Decorative "match" visual for the right-hand AuthLayout panel: a fictional
 * company publishes an offer, the handshake badge pulses, and the freelance
 * business card answers with a 98% match. Static data, no interaction, hidden
 * from assistive technologies.
 */
export const AuthMatchPanel = () => {
  const { t } = useI18n();

  // Latched on the animationend of the LAST reveal block, never on a mount
  // timer: below `lg` the panel is display:none, no animation runs, and the
  // first reveal must still play on the first resize above `lg`. Once
  // settled, the reveal classes are dropped so a display:none↔flex toggle
  // around the breakpoint cannot restart the choreography.
  const [settled, setSettled] = createSignal(false);

  const cardData = (): FreelanceBusinessCardData => ({
    fullName: t("auth.match_panel.freelance_name"),
    jobTitle: t("auth.match_panel.freelance_job_title"),
    residency: t("auth.match_panel.freelance_city"),
    skills: t("auth.match_panel.skills"),
    pricePerDay: 320,
    availabilityNow: true,
    workPreference: RemotePolicy.REMOTE,
  });

  return (
    <div
      aria-hidden="true"
      class="flex w-full max-w-sm flex-col items-stretch [@media(max-height:820px)]:scale-90"
    >
      {/* Offer published by the fictional company */}
      <Card
        class="flex items-start gap-3 p-4 [animation-delay:0.1s]"
        classList={{ "match-reveal": !settled() }}
      >
        <span class="grid size-10 shrink-0 place-items-center rounded-md bg-matisse-100 font-display font-bold text-matisse-800">
          {t("auth.match_panel.company_initial")}
        </span>
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-semibold leading-tight">
              {t("auth.match_panel.company_name")}
            </span>
            <span class="text-xs text-muted-foreground">
              {t("auth.match_panel.company_time")}
            </span>
          </div>
          <p class="text-sm leading-snug text-muted-foreground">
            {t("auth.match_panel.company_event")}
          </p>
          <Badge variant="secondary" class="w-fit gap-1.5">
            <TbOutlineBriefcase aria-hidden="true" />
            {t("auth.match_panel.offer_badge")}
          </Badge>
        </div>
      </Card>

      {/* Connector with the pulsing handshake badge */}
      <div
        class="flex flex-col items-center py-1 [animation-delay:0.35s]"
        classList={{ "match-reveal": !settled() }}
      >
        <div class="h-5 w-px bg-matisse-200" />
        <span class="relative my-1 grid size-10 place-items-center rounded-full bg-primary text-primary-foreground shadow-md shadow-primary/30">
          <span class="match-ring absolute -inset-1.5 rounded-full border-2 border-primary/35" />
          <FaSolidHandshake size={20} />
        </span>
        <div class="h-5 w-px bg-matisse-200" />
      </div>

      {/* The reusable business card, static, crowned by the match badge */}
      <div
        class="relative [animation-delay:0.55s]"
        classList={{ "match-reveal": !settled() }}
      >
        <FreelanceBusinessCard
          data={cardData()}
          animated={false}
          border="static"
        />
        <Badge class="absolute -top-3 right-4 border-transparent bg-green-100 px-2.5 text-green-800 shadow-sm">
          {t("auth.match_panel.match_badge")}
        </Badge>
      </div>

      {/* Other matching profiles: stacked avatars + counter */}
      <div
        class="mt-5 flex items-center justify-center gap-3 [animation-delay:0.75s]"
        classList={{ "match-reveal": !settled() }}
      >
        <div class="flex -space-x-2.5">
          <Index each={t("auth.match_panel.cluster_initials")}>
            {(initials, index) => (
              <span
                class="grid size-8 place-items-center rounded-full font-display text-xs font-bold ring-2 ring-matisse-50"
                classList={{
                  "bg-primary/10 text-primary": index % 2 === 0,
                  "bg-matisse-100 text-matisse-800": index % 2 === 1,
                }}
              >
                {initials()}
              </span>
            )}
          </Index>
        </div>
        <span class="text-xs font-medium text-matisse-800/80">
          {t("auth.match_panel.cluster_counter")}
        </span>
      </div>

      {/* Tagline — last block: its animationend settles the choreography */}
      <p
        class="mt-5 text-center text-xs leading-relaxed text-matisse-800/70 [animation-delay:0.9s]"
        classList={{ "match-reveal": !settled() }}
        onAnimationEnd={() => setSettled(true)}
      >
        {t("auth.match_panel.tagline")}
      </p>
    </div>
  );
};
